// Manage OpenAI "characters" — named voices that the TTS endpoint
// uses as conversation defaults. Each row is editable inline; one
// shared "new speaker" form lives above the table.

import React, { useEffect, useMemo, useState } from 'react';
import {
    createSpeaker,
    deleteSpeaker,
    getElevenLabsVoices,
    getSpeakers,
    patchSpeaker,
} from '../adminApi';
import { Loading, Err } from './Overview';

export default function Speakers() {
    const [data, setData] = useState(null);
    const [voices, setVoices] = useState(null);
    const [voicesErr, setVoicesErr] = useState(null);
    const [err, setErr] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setData(null);
            setErr(null);
            try {
                const d = await getSpeakers();
                if (!cancelled) setData(d);
            } catch (e) {
                if (!cancelled) setErr(e?.message || 'Failed to load');
            }
        })();
        return () => { cancelled = true; };
    }, [reloadKey]);

    // Voice list loaded once per mount — independent of speaker reloads,
    // since fetching from ElevenLabs is a network hop we don't want
    // to repeat on every save.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const v = await getElevenLabsVoices();
                if (!cancelled) setVoices(v?.items || []);
            } catch (e) {
                if (!cancelled) setVoicesErr(e?.response?.data?.error || e?.message || 'Failed to load voices');
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const refresh = () => setReloadKey((k) => k + 1);

    if (err) return <Err msg={err} />;

    return (
        <div>
            <h2 style={styles.h2}>Voice settings · speakers</h2>
            <p style={styles.intro}>
                Named ElevenLabs characters used when generating TTS for
                lines. Set the left and right speakers on a conversation
                to control the default voice; per-line overrides are
                available from each line's voice button.
            </p>

            {voicesErr ? (
                <div style={styles.voiceWarn}>
                    Couldn't load ElevenLabs voice library: {voicesErr}. You can still
                    paste a voice ID by hand.
                </div>
            ) : null}

            <NewSpeakerForm voices={voices} onCreated={refresh} />

            {!data ? <Loading /> : (
                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <Th>Name</Th>
                                <Th>Sex</Th>
                                <Th>Voice</Th>
                                <Th>Baseline instructions</Th>
                                <Th align="right">&nbsp;</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.length === 0 ? (
                                <tr>
                                    <Td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>
                                        No speakers yet. Create one above.
                                    </Td>
                                </tr>
                            ) : data.items.map((s) => (
                                <SpeakerRow key={s.id} speaker={s} voices={voices} onSaved={refresh} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function NewSpeakerForm({ voices, onCreated }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [sex, setSex] = useState('');
    const [voice, setVoice] = useState('');
    const [baseline, setBaseline] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const reset = () => {
        setName(''); setSex(''); setVoice(''); setBaseline(''); setError(null);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return setError('Name is required');
        if (!voice.trim()) return setError('Voice is required');
        try {
            setSaving(true);
            await createSpeaker({
                name: name.trim(),
                sex: sex || null,
                voice: voice.trim(),
                baseline_instructions: baseline.trim() || null,
            });
            reset();
            setOpen(false);
            onCreated && onCreated();
        } catch (err) {
            setError(err?.response?.data?.error || err?.message || 'Create failed');
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <div style={{ marginBottom: 12 }}>
                <button type="button" style={styles.btnPrimary} onClick={() => setOpen(true)}>
                    + New speaker
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} style={styles.newForm}>
            <div style={styles.newFormRow}>
                <input
                    style={styles.input}
                    placeholder="Name (e.g. Marco)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={saving}
                    autoFocus
                />
                <select style={styles.select} value={sex} onChange={(e) => setSex(e.target.value)} disabled={saving}>
                    <option value="">— sex —</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                </select>
                <VoicePicker value={voice} onChange={setVoice} sexFilter={sex} voices={voices} disabled={saving} />
            </div>
            <div style={styles.newFormRow}>
                <textarea
                    style={styles.textarea}
                    placeholder="Baseline instructions (only meaningful on eleven_v3; use audio tags like '[warm, slow]')"
                    value={baseline}
                    onChange={(e) => setBaseline(e.target.value)}
                    rows={2}
                    disabled={saving}
                />
            </div>
            {error ? <div style={styles.err}>{error}</div> : null}
            <div style={styles.newFormRow}>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>
                    {saving ? 'Creating…' : 'Create'}
                </button>
                <button
                    type="button"
                    style={styles.btnLink}
                    onClick={() => { reset(); setOpen(false); }}
                    disabled={saving}
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

function SpeakerRow({ speaker, voices, onSaved }) {
    const [draft, setDraft] = useState({
        name: speaker.name || '',
        sex: speaker.sex || '',
        voice: speaker.voice || '',
        baseline_instructions: speaker.baseline_instructions || '',
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const [savedTick, setSavedTick] = useState(false);

    const dirty = (
        draft.name !== (speaker.name || '')
        || draft.sex !== (speaker.sex || '')
        || draft.voice !== (speaker.voice || '')
        || draft.baseline_instructions !== (speaker.baseline_instructions || '')
    );

    const reset = () => {
        setDraft({
            name: speaker.name || '',
            sex: speaker.sex || '',
            voice: speaker.voice || '',
            baseline_instructions: speaker.baseline_instructions || '',
        });
        setError(null);
    };

    const onSave = async () => {
        if (!dirty || saving) return;
        try {
            setSaving(true);
            setError(null);
            await patchSpeaker(speaker.id, {
                name: draft.name.trim(),
                sex: draft.sex || null,
                voice: draft.voice.trim(),
                baseline_instructions: draft.baseline_instructions || null,
            });
            setSavedTick(true);
            setTimeout(() => setSavedTick(false), 1400);
            onSaved && onSaved();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        if (deleting) return;
        if (!window.confirm(`Delete speaker "${speaker.name}"? This will fail if any conversation or line still references it.`)) return;
        try {
            setDeleting(true);
            setError(null);
            await deleteSpeaker(speaker.id);
            onSaved && onSaved();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Delete failed');
            setDeleting(false);
        }
    };

    return (
        <tr style={dirty ? styles.rowDirty : undefined}>
            <Td>
                <input
                    style={styles.input}
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    disabled={saving || deleting}
                />
            </Td>
            <Td>
                <select
                    style={styles.select}
                    value={draft.sex}
                    onChange={(e) => setDraft((d) => ({ ...d, sex: e.target.value }))}
                    disabled={saving || deleting}
                >
                    <option value="">—</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                </select>
            </Td>
            <Td>
                <VoicePicker
                    value={draft.voice}
                    onChange={(v) => setDraft((d) => ({ ...d, voice: v }))}
                    sexFilter={draft.sex}
                    voices={voices}
                    disabled={saving || deleting}
                />
            </Td>
            <Td>
                <textarea
                    rows={2}
                    style={styles.textarea}
                    value={draft.baseline_instructions}
                    onChange={(e) => setDraft((d) => ({ ...d, baseline_instructions: e.target.value }))}
                    disabled={saving || deleting}
                />
            </Td>
            <Td align="right">
                {error ? <div style={styles.err}>{error}</div> : null}
                {savedTick ? <span style={styles.saved}>Saved ✓</span> : null}
                {dirty ? (
                    <>
                        <button type="button" style={styles.btnLink} onClick={reset} disabled={saving || deleting}>
                            Reset
                        </button>
                        <button type="button" style={styles.btnPrimary} onClick={onSave} disabled={saving || deleting}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </>
                ) : (
                    <button type="button" style={styles.btnDanger} onClick={onDelete} disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                )}
            </Td>
        </tr>
    );
}

function VoicePicker({ value, onChange, sexFilter, voices, disabled }) {
    // If the voice library failed to load we just show the free-text
    // input — operator can paste a voice_id from the ElevenLabs
    // dashboard.
    const filtered = useMemo(() => {
        if (!voices) return null;
        if (!sexFilter) return voices;
        const want = sexFilter.toLowerCase() === 'm' ? 'male' : 'female';
        return voices.filter((v) => {
            const gender = (v.labels?.gender || '').toLowerCase();
            // Show the voice if it matches OR if it has no gender label.
            return !gender || gender === want;
        });
    }, [voices, sexFilter]);

    const valueInList = !value || (filtered && filtered.some((v) => v.voice_id === value));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 260 }}>
            {filtered ? (
                <select
                    style={styles.select}
                    value={valueInList ? value : ''}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                >
                    <option value="">— voice —</option>
                    {filtered.map((v) => (
                        <option key={v.voice_id} value={v.voice_id}>
                            {voiceLabel(v)}
                        </option>
                    ))}
                </select>
            ) : null}
            <input
                style={{ ...styles.input, fontSize: 11, fontFamily: 'monospace' }}
                placeholder="voice_id (or pick from list above)"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                spellCheck={false}
            />
        </div>
    );
}

function voiceLabel(v) {
    const parts = [v.name];
    const labels = v.labels || {};
    const tags = [labels.gender, labels.accent, labels.descriptive, labels.age, labels.use_case]
        .filter(Boolean).join(' · ');
    if (tags) parts.push(`(${tags})`);
    return parts.join(' ');
}

function Th({ children, align }) {
    return <th style={{ ...styles.th, textAlign: align || 'left' }}>{children}</th>;
}
function Td({ children, align, style, colSpan }) {
    return <td colSpan={colSpan} style={{ ...styles.td, textAlign: align || 'left', ...(style || {}) }}>{children}</td>;
}

const styles = {
    h2: { marginTop: 0, color: '#222' },
    intro: { color: '#666', fontSize: 13, marginTop: -6, marginBottom: 14, lineHeight: 1.5 },
    tableWrap: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 12px', borderBottom: '1px solid #eee', color: '#888', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 },
    td: { padding: '10px 12px', borderBottom: '1px solid #f3f3f3', color: '#222', verticalAlign: 'top' },
    rowDirty: { backgroundColor: '#fff8ef' },
    input: { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' },
    select: { padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 13, backgroundColor: '#fff' },
    textarea: { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' },
    btnPrimary: { padding: '8px 14px', borderRadius: 6, border: '1px solid #d97447', background: '#d97447', color: '#fff', fontWeight: 600, cursor: 'pointer' },
    btnLink: { padding: '8px 0', border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', marginRight: 8 },
    btnDanger: { padding: '6px 12px', borderRadius: 6, border: '1px solid #c0392b', background: '#fff', color: '#c0392b', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
    newForm: { padding: 14, backgroundColor: '#fff', borderRadius: 12, marginBottom: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    newFormRow: { display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' },
    err: { color: '#c0392b', fontSize: 12, marginBottom: 4 },
    saved: { color: '#2e7d32', fontSize: 12, fontWeight: 600, marginRight: 6 },
    voiceWarn: {
        padding: '8px 12px',
        backgroundColor: '#fff3e0',
        color: '#9c4a04',
        border: '1px solid #ffd9b0',
        borderRadius: 8,
        fontSize: 12,
        marginBottom: 12,
    },
};
